/**
 * App Store subscriptions: the product catalogue, the App Store Server API,
 * and how a store subscription lands on the one `subscriptions` row that every
 * plan check reads. Only WebCrypto and fetch, so Deno and vitest both run it.
 *
 * Trust model: notification payloads and anything the app sends are treated
 * as pointers (a transaction id). The state itself always comes from Apple's
 * API over TLS, so no JWS certificate chain has to be verified here.
 */

export const APP_BUNDLE_ID = "io.cctvplanner.app";

/** Store price = web price in the same currency × this. Both stores and
 *  Stripe treat USD/CAD as tax-exclusive and the rest as tax-inclusive, so the
 *  factor holds for what the customer pays. */
export const APP_STORE_PRICE_FACTOR = 1.5;

export type StorePlan = "standard" | "premium" | "pro";
export type StorePeriod = "monthly" | "yearly";

/** Product ids as created in App Store Connect, one subscription group. */
export const STORE_PRODUCTS: Readonly<Record<string, { plan: StorePlan; period: StorePeriod }>> = {
  "io.cctvplanner.app.standard.monthly": { plan: "standard", period: "monthly" },
  "io.cctvplanner.app.standard.yearly": { plan: "standard", period: "yearly" },
  "io.cctvplanner.app.premium.monthly": { plan: "premium", period: "monthly" },
  "io.cctvplanner.app.premium.yearly": { plan: "premium", period: "yearly" },
  "io.cctvplanner.app.pro.monthly": { plan: "pro", period: "monthly" },
  "io.cctvplanner.app.pro.yearly": { plan: "pro", period: "yearly" },
};

export const APP_STORE_API = {
  Production: "https://api.storekit.itunes.apple.com",
  Sandbox: "https://api.storekit-sandbox.itunes.apple.com",
} as const;
export type AppStoreEnvironment = keyof typeof APP_STORE_API;

export type StoreStatus = "active" | "grace" | "billing_retry" | "expired" | "revoked";

/** `status` of a subscription in Get All Subscription Statuses. */
const APPLE_STATUS: Record<number, StoreStatus> = {
  1: "active",
  2: "expired",
  3: "billing_retry",
  4: "grace",
  5: "revoked",
};

export interface AppleTransaction {
  originalTransactionId: string;
  transactionId?: string;
  productId: string;
  bundleId: string;
  expiresDate?: number;
  appAccountToken?: string;
  revocationDate?: number;
  environment?: string;
}

export interface AppleRenewalInfo {
  autoRenewStatus?: number;
  gracePeriodExpiresDate?: number;
}

export interface AppleStatusResponse {
  environment?: string;
  bundleId?: string;
  data?: Array<{
    subscriptionGroupIdentifier?: string;
    lastTransactions?: Array<{
      originalTransactionId: string;
      status: number;
      signedTransactionInfo: string;
      signedRenewalInfo?: string;
    }>;
  }>;
}

/** One row of `store_subscriptions`, without the owner. */
export interface StoreSubscriptionRow {
  store: "app_store";
  environment: string;
  original_transaction_id: string;
  product_id: string;
  plan: StorePlan;
  period: StorePeriod;
  status: StoreStatus;
  auto_renew: boolean;
  expires_at: string | null;
  app_account_token: string | null;
}

/** The parts of a `subscriptions` row the projection looks at. */
export interface PlanRow {
  plan: string;
  status: string;
  billing_source?: string | null;
}

export type Projection =
  | { action: "none" }
  /** A paid web plan is active: the store plan is recorded, not applied. */
  | { action: "keep_web" }
  | { action: "write"; values: Record<string, unknown> };

const PLAN_RANK: Record<string, number> = { free: 0, lite: 1, standard: 2, premium: 3, pro: 4, enterprise: 5 };
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function base64UrlEncode(bytes: Uint8Array): string {
  let bin = "";
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function base64UrlDecode(text: string): Uint8Array {
  const b64 = text.replace(/-/g, "+").replace(/_/g, "/").padEnd(Math.ceil(text.length / 4) * 4, "=");
  const bin = atob(b64);
  return Uint8Array.from(bin, (c) => c.charCodeAt(0));
}

/** Payload of a compact JWS, without verifying it (see the trust model). */
export function decodeJwsPayload<T>(jws: string): T {
  const parts = jws.split(".");
  if (parts.length !== 3) throw new Error("not a compact JWS");
  return JSON.parse(new TextDecoder().decode(base64UrlDecode(parts[1]))) as T;
}

function pemToDer(pem: string) {
  const body = pem.replace(/-----(BEGIN|END) [A-Z ]+-----/g, "").replace(/\s+/g, "");
  return Uint8Array.from(atob(body), (c) => c.charCodeAt(0));
}

/** Bearer token for the App Store Server API, signed with the in-app purchase
 *  key (.p8) from App Store Connect. Valid for 20 minutes. */
export async function appStoreApiToken(opts: {
  keyId: string;
  issuerId: string;
  privateKeyPem: string;
  bundleId?: string;
  now?: number;
}): Promise<string> {
  const iat = Math.floor((opts.now ?? Date.now()) / 1000);
  const encode = (value: unknown) => base64UrlEncode(new TextEncoder().encode(JSON.stringify(value)));
  const input = `${encode({ alg: "ES256", kid: opts.keyId, typ: "JWT" })}.${encode({
    iss: opts.issuerId,
    iat,
    exp: iat + 20 * 60,
    aud: "appstoreconnect-v1",
    bid: opts.bundleId ?? APP_BUNDLE_ID,
  })}`;
  const key = await crypto.subtle.importKey(
    "pkcs8",
    pemToDer(opts.privateKeyPem),
    { name: "ECDSA", namedCurve: "P-256" },
    false,
    ["sign"],
  );
  // WebCrypto returns ECDSA signatures as r||s, which is what JWS expects.
  const signature = await crypto.subtle.sign({ name: "ECDSA", hash: "SHA-256" }, key, new TextEncoder().encode(input));
  return `${input}.${base64UrlEncode(new Uint8Array(signature))}`;
}

/** Get All Subscription Statuses for any transaction id of the subscription.
 *  Tries the hinted environment first, then the other one: App Review buys in
 *  the sandbox against the production app, so both have to resolve. Null when
 *  neither environment knows the transaction. */
export async function fetchSubscriptionStatuses(
  transactionId: string,
  token: string,
  opts: { environment?: string | null; fetchImpl?: typeof fetch } = {},
): Promise<{ environment: AppStoreEnvironment; response: AppleStatusResponse } | null> {
  const doFetch = opts.fetchImpl ?? fetch;
  const order: AppStoreEnvironment[] = opts.environment === "Sandbox" ? ["Sandbox", "Production"] : ["Production", "Sandbox"];
  for (const environment of order) {
    const res = await doFetch(`${APP_STORE_API[environment]}/inApps/v1/subscriptions/${encodeURIComponent(transactionId)}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.status === 404) continue;
    // Production refuses every token until the app's first release, while the
    // sandbox, where App Review buys, already answers: look there first.
    if (res.status === 401 && environment === "Production") continue;
    if (!res.ok) throw new Error(`App Store Server API ${environment} ${res.status}`);
    return { environment, response: (await res.json()) as AppleStatusResponse };
  }
  return null;
}

/** Rows for every subscription in the response that belongs to this app and
 *  sells a known product. */
export function rowsFromStatusResponse(
  response: AppleStatusResponse,
  environment: string,
  bundleId: string = APP_BUNDLE_ID,
): StoreSubscriptionRow[] {
  const rows: StoreSubscriptionRow[] = [];
  for (const group of response.data ?? []) {
    for (const last of group.lastTransactions ?? []) {
      const tx = decodeJwsPayload<AppleTransaction>(last.signedTransactionInfo);
      if (tx.bundleId !== bundleId) continue;
      const product = STORE_PRODUCTS[tx.productId];
      if (!product) continue;
      const renewal = last.signedRenewalInfo ? decodeJwsPayload<AppleRenewalInfo>(last.signedRenewalInfo) : {};
      let status = APPLE_STATUS[last.status] ?? "expired";
      if (tx.revocationDate) status = "revoked";
      // In the grace period the paid period is already over; access lasts
      // until the grace period does.
      const accessEnd = status === "grace" && renewal.gracePeriodExpiresDate ? renewal.gracePeriodExpiresDate : tx.expiresDate;
      const token = tx.appAccountToken && UUID.test(tx.appAccountToken) ? tx.appAccountToken.toLowerCase() : null;
      rows.push({
        store: "app_store",
        environment: tx.environment ?? environment,
        original_transaction_id: last.originalTransactionId,
        product_id: tx.productId,
        plan: product.plan,
        period: product.period,
        status,
        auto_renew: renewal.autoRenewStatus !== 0,
        expires_at: accessEnd ? new Date(accessEnd).toISOString() : null,
        app_account_token: token,
      });
    }
  }
  return rows;
}

function isLive(row: Pick<StoreSubscriptionRow, "status" | "expires_at">, now: number): boolean {
  if (row.status !== "active" && row.status !== "grace") return false;
  return !row.expires_at || Date.parse(row.expires_at) > now;
}

/** What the store rows of one user mean for their `subscriptions` row.
 *
 *  - A live store subscription becomes the plan, unless a paid web plan is
 *    active (then both are paying; the web plan stays and the caller flags it).
 *  - When no store subscription is live, a row the store wrote lapses the way
 *    Stripe lapses one: billing retry is past_due, anything else canceled. The
 *    plan name stays, as with Stripe. A row the store never wrote is left alone.
 *  - Rows from both stores count; billing_source names the store of the plan
 *    that wins.
 */
export function projectStoreEntitlement(
  current: PlanRow | null,
  rows: ReadonlyArray<
    Pick<StoreSubscriptionRow, "plan" | "period" | "status" | "auto_renew" | "expires_at"> & { store?: string | null }
  >,
  now: number = Date.now(),
): Projection {
  const live = rows
    .filter((r) => isLive(r, now))
    .sort((a, b) => (PLAN_RANK[b.plan] - PLAN_RANK[a.plan]) || (Date.parse(b.expires_at ?? "") - Date.parse(a.expires_at ?? "")));
  const best = live[0];
  const source = current?.billing_source ?? "stripe";
  const webPaid = !!current && source === "stripe" && current.plan !== "free" &&
    (current.status === "active" || current.status === "trialing");

  if (best) {
    if (webPaid) return { action: "keep_web" };
    return {
      action: "write",
      values: {
        plan: best.plan,
        period: best.period,
        status: "active",
        current_period_end: best.expires_at,
        cancel_at_period_end: !best.auto_renew,
        canceled_at: null,
        billing_source: best.store === "play" ? "play" : "app_store",
        // A finished Stripe subscription must not be able to touch this row
        // again through a late event matched on its id.
        stripe_subscription_id: null,
      },
    };
  }

  if (!current || (source !== "app_store" && source !== "play")) return { action: "none" };
  const latest = [...rows].sort((a, b) => Date.parse(b.expires_at ?? "") - Date.parse(a.expires_at ?? ""))[0];
  const status = latest?.status === "billing_retry" ? "past_due" : "canceled";
  if (current.status === status) return { action: "none" };
  return {
    action: "write",
    values: {
      status,
      cancel_at_period_end: false,
      canceled_at: status === "canceled" ? new Date(now).toISOString() : null,
    },
  };
}
